import * as React from "react";
import LevelFilter from "../../../components/InstallationFilterComponents/levelFilter";
import ConsumptionFilter from "../../../components/InstallationFilterComponents/ConsumptionFilter";
import BaseFilter from "../../../core/models/BaseFilter";
import clsx from "clsx";
import {
  makeStyles,
  Theme,
  createStyles,
  Card,
  Grid,
  Typography,
  Hidden,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import MaterialSelect from "../../../components/MaterialSelect";
import InstallationLevelHistoryFilter from "../../../components/InstallationFilterComponents/LevelHistoryFilter";
import TankModel from "../../../core/models/Tank";
import InstallationLevelConsumptionFilter from "../../../components/InstallationFilterComponents/LevelConsumptionFilter";
import {
  tabLevelChartLabel,
  tabConsumptionChartLabel,
  tabLevelHistoryLabel,
  tabConsumptionHistoryLabel,
  tabTripsHistoryLabel,
} from "../InstallationMainCard";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useHubx } from "../../../HubX";
import { EVENT_REFRESH_INSTALLATION_DETAIL_PAGE } from "../../../Events";
import InstallationTripsHistoryFilter from "../../../components/InstallationFilterComponents/TripsHistoryFilter";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "background": theme.customPaper.background.cardBackgroundLight,
      "padding": theme.spacing(2),

      "& .selector": {
        display: "flex",
        alignItems: "center",
        color: theme.palette.text.secondary,

        [theme.breakpoints.down("xs")]: {
          "flexDirection": "column",
          "alignItems": "flex-start",

          "& > div": {
            margin: 0,
            width: "100%",
          },
        },
      },

      [theme.breakpoints.down("xs")]: {
        "&.MuiPaper-elevation1": {
          boxShadow: "unset",
        },
      },
    },

    gridRoot: {
      alignItems: "center",
      columnGap: theme.spacing(1),
    },

    filtersWrapper: {
      flex: 1,

      [theme.breakpoints.down("xs")]: {
        marginTop: theme.spacing(3),
      },
    },

    childrenWrapper: {
      "display": "flex",
      "alignItems": "center",
      "justifyContent": "flex-end",
      "flex": 1,

      "& > *:not(first-child)": {
        marginLeft: theme.spacing(4),

        [theme.breakpoints.down("xs")]: {
          marginLeft: 0,
        },
      },

      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        alignItems: "flex-start",
        rowGap: `${theme.spacing(3)}px`,
      },
    },

    accordion: {
      "background": theme.customPaper.background.cardBackgroundLight,

      "& .MuiAccordionSummary-root": {
        borderBottom: `1px solid ${theme.customPalette.border.gray}`,
        minHeight: "unset",
      },

      "& .MuiAccordionSummary-content": {
        margin: 0,
      },

      "& .MuiIconButton-root": {
        padding: 0,
      },

      "& .MuiAccordionDetails-root": {
        padding: 0,
      },
    },

    refreshButton: {
      "backgroundColor": theme.customPaper.background.cardBackgroundLightContrast,

      "&:hover": {
        "& svg": {
          color: theme.palette.primary.main,
        },
      },
    },

    hidden: {
      display: "none",
    },
  })
);

type InstallationFilterProps = {
  selectedTab: number | string;
  hasMoreThanOneTank?: boolean;
  isLoading: boolean;
  installationFilter?: BaseFilter;
  customerFilter?: BaseFilter;
  tanks?: TankModel[];
};

function InstallationFilter(props: InstallationFilterProps) {
  const {
    selectedTab,
    hasMoreThanOneTank,
    installationFilter,
    customerFilter,
    tanks,
    isLoading = false,
  } = props;

  const classes = useStyles();

  const hub = useHubx();

  const handleClickRefresh = () => {
    hub.notify(EVENT_REFRESH_INSTALLATION_DETAIL_PAGE);
  };

  const mainRender = () => {
    return (
      <Card className={classes.root}>
        <Grid className={classes.gridRoot} container>
          <Grid item sm="auto" xs={12}>
            <div className={"selector"}>
              {customerFilter && !isLoading && (
                <>
                  <Typography> Grupo de Instalações: </Typography>
                  <MaterialSelect filter={customerFilter} />
                </>
              )}
              {isLoading && <Skeleton width={170} height={40} />}
            </div>
          </Grid>

          <Grid item sm xs={12}>
            <div className={"selector"}>
              {installationFilter && !isLoading && (
                <>
                  <Typography> Instalação: </Typography>
                  <MaterialSelect filter={installationFilter} />
                </>
              )}
              {isLoading && <Skeleton width={170} height={40} />}
            </div>
          </Grid>

          <Grid item className={classes.filtersWrapper}>
            <div
              className={clsx(
                selectedTab !== tabLevelChartLabel && classes.hidden,
                classes.childrenWrapper
              )}
            >
              <LevelFilter hasMoreThanOneTank={hasMoreThanOneTank} />
            </div>

            <div
              className={clsx(
                selectedTab !== tabConsumptionChartLabel && classes.hidden,
                classes.childrenWrapper
              )}
            >
              <ConsumptionFilter />
            </div>

            <div
              className={clsx(
                selectedTab !== tabLevelHistoryLabel && classes.hidden,
                classes.childrenWrapper
              )}
            >
              <InstallationLevelHistoryFilter
                tanks={tanks}
                hasMoreThanOneTank={hasMoreThanOneTank}
              />
            </div>
            
            <div
              className={clsx(
                selectedTab !== tabTripsHistoryLabel && classes.hidden,
                classes.childrenWrapper
              )}
            >
              <InstallationTripsHistoryFilter
                tanks={tanks}
                hasMoreThanOneTank={hasMoreThanOneTank}
              />
            </div>

            <div
              className={clsx(
                selectedTab !== tabConsumptionHistoryLabel && classes.hidden,
                classes.childrenWrapper
              )}
            >
              <InstallationLevelConsumptionFilter />
            </div>
          </Grid>
          <Grid sm="auto" xs={12}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Tooltip title="Clique para atualizar os dados da página">
                <IconButton className={classes.refreshButton} onClick={handleClickRefresh}>
                  <RefreshIcon color="action" />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Card>
    );
  };

  return (
    <>
      <Hidden smUp>
        <Accordion className={classes.accordion}>
          <AccordionSummary>Refinar Visualização</AccordionSummary>

          <AccordionDetails>{mainRender()}</AccordionDetails>
        </Accordion>
      </Hidden>

      <Hidden xsDown>{mainRender()}</Hidden>
    </>
  );
}

export default InstallationFilter;
